import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Rocket } from 'lucide-react';
import { garageAPI } from '../../../services/garage/api';
import config from '../../../config/environment';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import ImageUrlOrUpload from '../components/ImageUrlOrUpload';
import {
  ROLE_OPTIONS,
  JOURNEY_OPTIONS,
  PERSONA_OPTIONS,
  CAPABILITY_OPTIONS,
} from '../constants/formOptions';
import '../AIGarage.css';

/** Map REACT_APP_ENVIRONMENT / deploy name → catalog stamp (PRD|TEST|DEV). */
function resolveDeployCatalogEnvironment(raw) {
  const env = String(raw || '').toLowerCase().trim();
  if (['production', 'prod', 'prd'].includes(env)) return 'PRD';
  if (['test', 'qa', 'staging'].includes(env)) return 'TEST';
  if (['dev', 'development'].includes(env)) return 'DEV';
  return null;
}

const PublishGemModal = ({
  isOpen,
  idea,
  adminEmail,
  adminName,
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [persona, setPersona] = useState('');
  const [personas, setPersonas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [tags, setTags] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [makerEmail, setMakerEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const deployEnvironment = resolveDeployCatalogEnvironment(config.ENVIRONMENT);
  const defaultMakerEmail = idea?.assignedMakerEmail || idea?.requesterEmail || adminEmail || '';
  const requesterLabel = idea?.requesterName || idea?.requesterEmail || '';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !idea) return;
    setTitle(idea.title || '');
    setDescription(idea.description || '');
    setUrl(idea.gemUrl || '');
    setPersona(idea.primaryPersona || '');
    setPersonas(idea.personas?.length ? idea.personas : (idea.primaryPersona ? [idea.primaryPersona] : []));
    setDepartments(Array.isArray(idea.departments) ? idea.departments : []);
    setRoles([]);
    setCapabilities([]);
    setTags([]);
    setJourneys([]);
    setImageUrl('');
    setMakerEmail(defaultMakerEmail);
    setError(null);
    setShowAdvanced(false);
    setSubmitting(false);
  }, [isOpen, idea, defaultMakerEmail]);

  useEffect(() => {
    if (!isOpen) return;
    garageAPI.getConfigOptions('department')
      .then((opts) => setDepartmentOptions((opts || []).map((o) => ({
        value: o.value,
        label: o.label || o.value,
      }))))
      .catch(() => setDepartmentOptions([]));
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, submitting]);

  if (!isOpen || !idea) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !url.trim() || !persona.trim()) {
      setError('Title, description, Gem URL, and primary persona are required.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const result = await garageAPI.publishGemFromIdea(idea.id, {
        title: title.trim(),
        description: description.trim(),
        url: url.trim(),
        persona: persona.trim(),
        personas,
        departments,
        roles,
        capabilities,
        tags,
        journeys,
        imageUrl: imageUrl.trim() || null,
        makerEmail: (makerEmail || defaultMakerEmail || '').trim() || null,
      });
      onSuccess?.(result);
      onClose();
    } catch (err) {
      console.error('Failed to publish Gem:', err);
      const detail = err?.response?.data?.detail;
      let message = 'Failed to publish Gem. Please try again.';
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail?.message) {
        message = detail.message;
        if (detail.existingToolTitle) {
          message += ` (“${detail.existingToolTitle}”)`;
        }
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div className="garage-modal-overlay" onClick={() => !submitting && onClose()}>
      <div
        className="garage-modal garage-publish-gem-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="garage-modal-header">
          <div className="garage-modal-title-row">
            <Rocket size={20} className="garage-modal-icon" />
            <h2>Publish Gem</h2>
          </div>
          <button
            className="garage-modal-close"
            onClick={onClose}
            disabled={submitting}
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <div className="garage-request-info-context">
            <p>
              <strong>Request:</strong> {idea.title}
            </p>
            <p>
              Creates a published Gem in Discover and marks this request as published.
              {idea.existingToolId ? ' This will update the linked existing Gem.' : ''}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="garage-form-group">
              <label className="garage-label">
                Title <span className="required">*</span>
              </label>
              <input
                className="garage-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            <div className="garage-form-group">
              <label className="garage-label">
                Description <span className="required">*</span>
              </label>
              <textarea
                className="garage-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                disabled={submitting}
                required
              />
            </div>

            <div className="garage-form-group">
              <label className="garage-label">
                Gem URL <span className="required">*</span>
              </label>
              <input
                className="garage-input"
                type="url"
                placeholder="https://gemini.google.com/gem/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={submitting}
                required
              />
            </div>

            <div className="garage-form-group">
              <label className="garage-label">
                Primary persona <span className="required">*</span>
              </label>
              <select
                className="garage-select"
                value={persona}
                onChange={(e) => {
                  const next = e.target.value;
                  setPersona(next);
                  if (next && !personas.includes(next)) {
                    setPersonas([next, ...personas]);
                  }
                }}
                disabled={submitting}
                required
              >
                <option value="">Select persona…</option>
                {PERSONA_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
                {persona && !PERSONA_OPTIONS.includes(persona) && (
                  <option value={persona}>{persona}</option>
                )}
              </select>
            </div>

            <div className="garage-form-group">
              <label className="garage-label">Environment</label>
              <input
                className="garage-input"
                type="text"
                value={deployEnvironment || 'Not set for this deployment'}
                disabled
                readOnly
              />
              <p className="garage-help-text">
                Pre-set from this Garage deployment
                {config.ENVIRONMENT ? ` (${config.ENVIRONMENT})` : ''} so the Gem
                appears in the matching Discover catalog.
              </p>
            </div>

            <div className="garage-form-group">
              <label className="garage-label">Maker email</label>
              <input
                className="garage-input"
                type="email"
                value={makerEmail}
                onChange={(e) => setMakerEmail(e.target.value)}
                disabled={submitting}
                placeholder={defaultMakerEmail || 'maker@ringcentral.com'}
              />
              <p className="garage-help-text">
                Credited as the creator in Discover — defaults to the requester
                {requesterLabel ? ` (${requesterLabel})` : ''}. You
                {adminName ? ` (${adminName})` : ''} are recorded as the publisher.
              </p>
            </div>

            <button
              type="button"
              className="garage-btn garage-btn-link garage-publish-gem-toggle"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide catalog details' : 'Add catalog details (roles, tags…)'}
            </button>

            {showAdvanced && (
              <div className="garage-publish-gem-advanced">
                <MultiSelectDropdown
                  label="Target personas"
                  options={PERSONA_OPTIONS}
                  value={personas}
                  onChange={setPersonas}
                />
                <MultiSelectDropdown
                  label="Departments"
                  options={departmentOptions}
                  value={departments}
                  onChange={setDepartments}
                  helpText="Discover Mode B access for users in these departments"
                />
                <MultiSelectDropdown
                  label="Roles"
                  options={ROLE_OPTIONS}
                  value={roles}
                  onChange={setRoles}
                />
                <MultiSelectDropdown
                  label="Capabilities"
                  options={CAPABILITY_OPTIONS}
                  value={capabilities}
                  onChange={setCapabilities}
                />
                <MultiSelectDropdown
                  label="Journeys"
                  options={JOURNEY_OPTIONS}
                  value={journeys}
                  onChange={setJourneys}
                />
                <MultiSelectDropdown
                  label="Tags"
                  options={[]}
                  value={tags}
                  onChange={setTags}
                  allowCustom
                  placeholder="Add tags…"
                />
                <ImageUrlOrUpload
                  label="Image URL"
                  value={imageUrl}
                  onChange={setImageUrl}
                  disabled={submitting}
                  placeholder="https://…"
                />
              </div>
            )}

            {error && <div className="garage-error-message">{error}</div>}

            <div className="garage-modal-actions">
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="garage-btn garage-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Publishing…' : 'Publish Gem'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PublishGemModal;
